import {Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, model, viewChild} from '@angular/core';
import {WjFlexGridFilter, WjGridFilterModule} from '@mescius/wijmo.angular2.grid.filter';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {WjGridSearchModule} from '@mescius/wijmo.angular2.grid.search';
import {ColumnDeclaration, Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {WjGridColumnChooser} from '../../lib/wjgrid-columchooser';
import {CollectionView, DataType} from '@mescius/wijmo';
import {KpiResponse, PortfolioApi} from '../../../api-client-kt/src';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {DataDictionaryService, KpiDefinition, KpiWithDescription} from '../../data/data-dictionary';
import {WjNavModule} from '@mescius/wijmo.angular2.nav';
import {WjInputModule} from '@mescius/wijmo.angular2.input';
import {FormsModule} from '@angular/forms';
import {FluentComboboxValueAccessor} from '../../directives/fluent-ui-value-accessors.directive';
import {PortfolioDataContext} from '../../data/portfolio-data-context.service';

const kpiSegColumns: ColumnDeclaration[] = [
    {header:"Position", binding: 'position', dataType: DataType.Number, visible: false, width: 40, align: 'center', format: 'n0'},
    {header:"Office",binding:"officeName",dataType:DataType.String, visible: true, width: 90 },
    {header:"Office Key",binding:"officeKey",dataType:DataType.String, visible: false, width: 40},
    {header:"LoB",binding:"lobName",dataType:DataType.String, visible: true, width: 90},
    {header:"LoB Key",binding:"lobKey",dataType:DataType.String, visible: false, width: 40},
    {header:"Key", binding:"attribute",dataType:DataType.String, visible: false, width: 120},
    {header:"Name", binding:"name",dataType:DataType.String, visible: true, width: 150},
    {header:"Definition", binding:"definition",dataType: DataType.String, visible: true, wordWrap: true, width: 250},
    {header:"Weight", binding:"weight",dataType:DataType.Number, format: 'p0', visible: true, align: 'center', width: 60},
    {header:"Input Value", binding:"inputValue",dataType: DataType.String, visible: true, wordWrap: true, width: 160},
    {header:"Direction", binding:"directionIcon",dataType:DataType.Number, format: 'N0', visible: true, align: 'center', isContentHtml: true, width: 60 },
    {header:"Discrete", binding:"isDiscrete",dataType: DataType.String, isContentHtml: true, visible: true, align: 'center', width: 60},
    {header:"Interpretation", binding:"interpretation",dataType: DataType.String, visible: true, wordWrap: true, width: 300},
];

/**
 * Component for and displaying KPI configurations for the various portfolio segments.
 * Queries the collection of KPI data and allows filter for specific LOB and Office keys.
 */
@Component({
    selector: 'app-portfolio-config-kpis',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    standalone: true,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        WjGridFilterModule,
        WjGridModule,
        WjGridSearchModule,
        WjNavModule,
        WjInputModule,
        FormsModule,
        FluentComboboxValueAccessor
    ],
    templateUrl: './portfolio-config-kpis.component.html',
    styleUrl: './portfolio-config-kpis.component.scss'
})
export class PortfolioConfigKpisComponent {

    protected readonly Wijmo = Wijmo;

    protected readonly kpiSegmentData = new CollectionView<KpiWithDescription>([]);

    private readonly pfData = inject(PortfolioDataContext);
    private readonly pfSegData = inject(PortfolioSegmentDataContext);
    private readonly pfApi = inject(PortfolioApi);

    protected readonly lobs = this.pfData.lobs;
    protected readonly offices = this.pfData.offices;

    protected lobKey = model<string>(this.pfSegData.activeSegment.value()?.lobKey ?? '');
    protected officeKey = model<string>(this.pfSegData.activeSegment.value()?.officeKey ?? '');

    private readonly kpiSegmentGridFilter = viewChild<WjFlexGridFilter>('kpiSegmentGridFilter');

    constructor() {
        effect(() => this.alignKpiFiltersWithOfficeAndLob());
        effect(() => this.onActiveSegmentChange());
    }

    private lobKeyToName(key: string | undefined): string | undefined {
        return this.pfData.lobs.find(l => l.key === key)?.name;
    }

    private officeKeyToName(key: string | undefined): string | undefined {
        return this.pfData.offices.find(o => o.key === key)?.name;
    }

    private onActiveSegmentChange(): void {
        const segment = this.pfSegData.activeSegment.value();
        if (segment) {
            this.officeKey.set(segment?.officeKey);
            this.lobKey.set(segment?.lobKey);
        } else {
            this.officeKey.set('');
            this.lobKey.set('');
        }
    }

    private applySingleValueFilter(gridFilter: WjFlexGridFilter | undefined, columnName: string, value: string | undefined): void {
        const filter = gridFilter?.getColumnFilter(columnName)!;
        if (filter) {
            if (!value) {
                filter.valueFilter.clear();
            } else {
                filter.valueFilter.showValues = {[value]: true};
            }
        }
    }

    private alignKpiFiltersWithOfficeAndLob(): void {
        this.applySingleValueFilter(this.kpiSegmentGridFilter(), 'lobName', this.lobKeyToName(this.lobKey()));
        this.applySingleValueFilter(this.kpiSegmentGridFilter(), 'officeName', this.officeKeyToName(this.officeKey()));
        this.kpiSegmentGridFilter()?.apply();
    }

    async onKpiSegmentGridInitialized(grid: WjFlexGrid) {
        WjGridUtils.addColumns(grid, kpiSegColumns);
        WjGridColumnChooser.addColumnChooser('portfolio.kpis.segment', grid);
        this.alignKpiFiltersWithOfficeAndLob();

        grid.autoSizeRows(0, grid.columnHeaders.rows.length - 1, false, 10);
        grid.autoSizeRows();

        // put the load in the next loop and delay a little, because otherwise our
        // filters are not yet active, and for a small moment, one sees flickering
        // because the grid displays all the possible KPIs
        setTimeout(this.loadKpiData.bind(this), 100);
    }

    private async loadKpiData() {
        const kpiResponse = await this.pfApi.getKpis({ id: this.pfSegData.portfolioId()! })
        this.kpiSegmentData.sourceCollection = kpiResponse.map(k => this.toKpiWithDescription(k));
    }

    private toKpiWithDescription(kpi: KpiResponse): KpiWithDescription {
        const office = this.pfData.offices.find(o => o.key === kpi.officeKey);
        const lob = this.pfData.lobs.find(l => l.key === kpi.lobKey);
        const kpiDefinition = DataDictionaryService.getKpiDefinition(kpi.attribute);

        return {
            ...kpi,
            lobName: lob ? lob.name : '',
            officeName: office ? office.name : '',
            name: kpiDefinition?.name || '',
            description: kpiDefinition?.name || '',
            definition: kpiDefinition?.definition || '',
            directionIcon: DataDictionaryService.kpiDirectionIcon(kpi.directionUp),
            inputValue: kpiDefinition?.inputValue || '',
            interpretation: kpiDefinition?.interpretation || '',
            isDiscrete: this.discreteVisualElement(kpiDefinition)
        };
    }

    private discreteVisualElement(kpi: KpiDefinition | undefined): string {
        if (!kpi) return '';
        if (!kpi.isDiscrete) return '';
        return `<i class="fad fa-stairs" title="This KPI is discrete and does not have a continuous range of values"></i>`;
    }

    protected onClearComboboxValues() {
        this.lobKey.set('');
        this.officeKey.set('');
    }
}
